import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, Chip, FormControl, InputLabel, Select, MenuItem,
  TextField, Button, CircularProgress, Stack, Tooltip,
} from '@mui/material';
import { Refresh, FilterAltOff } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ACTION_COLOR = {
  login: 'success', logout: 'default', login_failed: 'error',
  create: 'primary', update: 'info', delete: 'error',
  password_changed: 'warning',
};

const fmt = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

export default function ActivityLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [limit,   setLimit]   = useState(50);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    user_id: '', module: '', action: '', from: '', to: '', search: '',
  });
  const [opts, setOpts] = useState({ modules: [], actions: [], users: [] });

  const loadFilters = useCallback(async () => {
    try {
      const { data } = await api.get('/activity-logs/filters');
      setOpts({ modules: data.modules || [], actions: data.actions || [], users: data.users || [] });
    } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page + 1);
      params.append('limit', limit);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await api.get(`/activity-logs?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [page, limit, filters]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { load(); }, [load]);

  const updateFilter = (k, v) => { setPage(0); setFilters(f => ({ ...f, [k]: v })); };
  const clearFilters = () => {
    setPage(0);
    setFilters({ user_id: '', module: '', action: '', from: '', to: '', search: '' });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Activity Log</Typography>
        <Typography variant="body2" color="text.secondary">
          Audit trail of all user actions {isAdmin ? '— viewing all users' : '— viewing your activity only'}.
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>User</InputLabel>
              <Select value={filters.user_id} label="User"
                onChange={e => updateFilter('user_id', e.target.value)}>
                <MenuItem value="">All Users</MenuItem>
                {opts.users.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name || u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Module</InputLabel>
            <Select value={filters.module} label="Module"
              onChange={e => updateFilter('module', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {opts.modules.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Action</InputLabel>
            <Select value={filters.action} label="Action"
              onChange={e => updateFilter('action', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {opts.actions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={filters.from} onChange={e => updateFilter('from', e.target.value)} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={filters.to} onChange={e => updateFilter('to', e.target.value)} />
          <TextField size="small" label="Search"
            placeholder="email, path, description, entity id"
            value={filters.search} onChange={e => updateFilter('search', e.target.value)}
            sx={{ minWidth: 240, flex: 1 }} />
          <Button variant="outlined" startIcon={<FilterAltOff />} onClick={clearFilters}>
            Clear
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={load}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>Time</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>IP</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                No activity matches the filters.
              </TableCell></TableRow>
            ) : rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{fmt(r.created_at)}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.user_name || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.user_email || ''}</Typography>
                </TableCell>
                <TableCell><Chip label={r.module} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Chip label={r.action} size="small"
                    color={ACTION_COLOR[r.action] || 'default'}
                    variant={ACTION_COLOR[r.action] ? 'filled' : 'outlined'} />
                </TableCell>
                <TableCell>
                  {r.entity_type ? (
                    <Typography variant="caption">
                      {r.entity_type}{r.entity_id ? ` #${r.entity_id}` : ''}
                    </Typography>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Tooltip title={r.path || ''} arrow>
                    <Typography variant="body2" sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.description || '—'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{r.ip_address || '—'}</Typography></TableCell>
                <TableCell align="center">
                  <Chip label={r.status_code || '—'} size="small"
                    color={r.status_code >= 400 ? 'error' : r.status_code >= 200 ? 'success' : 'default'}
                    variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={e => { setLimit(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>
    </Box>
  );
}
